import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { EditorType, ZoomType } from '../constants';
import {
	addPage,
	getPageHtml,
	parseIndesignEditorPage,
	setPageData,
} from './sources';
import { updateThumbnailsIfNeeded } from './thumbnails';
import { zoomPage } from './ui';
import { addToUndos } from './history';
import { Source } from '../reducer/history';
import * as selectors from '../selectors';
import * as helpers from '../utils/helpers';
import htmlEditorUtils from '../utils/htmlEditor';
import indesignEditorUtils from '../utils/indesignEditor';
import { createNotification } from '../../NotificationBar/actions';
import Utils from '../../../utils';
import ApiUtils from '../../../utils/api';

export enum PagesControllerActionKeys {
	SET_PAGE_SPREADS = '@@editor/pagesController/SET_PAGE_SPREADS',
	CHANGE_PAGE_SPREAD = '@@editor/pagesController/CHANGE_PAGE_SPREAD',
}

interface SetPageSpreadsAction extends Action {
	type: PagesControllerActionKeys.SET_PAGE_SPREADS;
	spreads: number[][];
}

interface ChangePageSpreadAction extends Action {
	type: PagesControllerActionKeys.CHANGE_PAGE_SPREAD;
	spreadIndex: number;
}

export type PagesControllerAction =
	SetPageSpreadsAction |
	ChangePageSpreadAction;

export const setPageSpreads = (
	pagesData: { showAlone: boolean }[],
	pageDimensions?: { width: number, height: number },
) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			pagesController: { displayMode, firstFrameMode, totalPages },
		},
	} = getState();

	const spreads = helpers.getPageSpreads(displayMode, firstFrameMode, totalPages, pagesData, pageDimensions);

	const action: SetPageSpreadsAction = {
		type: PagesControllerActionKeys.SET_PAGE_SPREADS,
		spreads,
	};
	dispatch(action);
};

export const changePageSpread = (
	spreadIndex: number,
	shouldAddToUndos: boolean = true,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	if (shouldAddToUndos) {
		const {
			editor: {
				pagesController: { currentSpreadIndex },
			},
		} = getState();

		dispatch(addToUndos({
			source: Source.CHANGE_SPREAD,
			data: { currentSpreadIndex },
		}));
	}

	const action: ChangePageSpreadAction = {
		type: PagesControllerActionKeys.CHANGE_PAGE_SPREAD,
		spreadIndex,
	};
	dispatch(action);

	const {
		editor: {
			pages,
			pagesController: { spreads, prevSpreadIndex },
			isAccessibleVersion,
		},
	} = getState();

	const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
	const prevSpread = !isAccessibleVersion ? spreads[prevSpreadIndex] : [prevSpreadIndex + 1];
	const newSpread = !isAccessibleVersion ? spreads[spreadIndex] : [spreadIndex + 1];
	const { width, height } = pages[prevSpread[0]];

	dispatch(zoomPage(ZoomType.FIT));

	if (isAccessibleVersion) {
		await dispatch(addPage(spreadIndex + 1, width, height));
		await dispatch(getPageHtml(spreadIndex + 1, resourceId));
	} else {
		await Promise.all(newSpread.map((page) => {
			dispatch(addPage(page, width, height));
			dispatch(getPageHtml(page, resourceId));
		}));
	}

	dispatch(updateThumbnailsIfNeeded(clientId, resourceId));
};

export const savePage = (page: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const requestId = `save-page-${page}`;
		Utils.cancelRequest(requestId);

		const {
			editor: {
				type,
				pages: { [page]: { defaultHtml, html } },
				configs: { pagesData: { [page - 1]: { sliders } } },
				spreadBackground: { pagesData: backgroundData },
				isAccessibleVersion,
			},
		} = getState();

		const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
		const editableData = {
			editableTextFields: selectors.getPageEditableTextFields(getState(), page),
			editableMedia: selectors.getPageEditableMedia(getState(), page),
			editableLinks: selectors.getPageEditableLinks(getState(), page),
			editableButtons: selectors.getPageEditableButtons(getState(), page),
			editableIframes: selectors.getPageEditableIframes(getState(), page),
			editableEnrichments: selectors.getPageEditableEnrichments(getState(), page),
			editableSliders: selectors.getPageEditableSliders(getState(), page),
		};
		const backgroundDeps = backgroundData[page] && backgroundData[page].deps;

		let htmlToSave;

		if (type === EditorType.HTML) {
			htmlToSave = htmlEditorUtils.getPageHtmlToSave(defaultHtml, editableData);
		} else if (isAccessibleVersion) {
			htmlToSave = indesignEditorUtils.getAccessiblePageHtmlToSave(defaultHtml, editableData);
		} else {
			htmlToSave = indesignEditorUtils.getPageHtmlToSave(defaultHtml, editableData, sliders, backgroundDeps);
		}

		if (isAccessibleVersion) {
			await ApiUtils.saveEditedAccessiblePage(clientId, resourceId, page, htmlToSave, requestId);
			await dispatch(parseIndesignEditorPage(page, htmlToSave, resourceId, undefined));
		} else if (type === EditorType.HTML) {
			await ApiUtils.saveHtmlEditorPage(clientId, resourceId, htmlToSave);
		} else {
			await ApiUtils.saveIndesignEditorPage(clientId, resourceId, page, htmlToSave, requestId);
		}

		if (!isAccessibleVersion) {
			dispatch(setPageData({
				page,
				defaultHtml: htmlToSave,
				html,
			}));
		}
	} catch (error) {
		// status is 0 for cancelled request, no need to display a notification
		if (error.status !== 0) {
			dispatch(createNotification('error.save_editor_data_error'));
		}
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/pagesController.ts